import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isEmpty } from "lodash";

import { useFetchSubstage } from "../../ClipboardScore/hooks/useFetchSubstage";
import { setBasketSizeWarningAcknowledgment } from "../../ClipboardScore/hooks/useIsBasketSizeWarningAcknowledgmentNeeded";
import { SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { convertModalShiftDataToUseBookShiftParams } from "../Booking/convertModalShiftDataToUseBookShiftParams";
import { PreBookingCheck, useBookShift } from "../Booking/useBookShift";
import { type ModalShiftData } from "../types";

interface BasketSizeWillReachWarningProps {
  modalState: UseModalState;
  shift: ModalShiftData;
  rateNegotiationId: string | undefined;
}

export function BasketSizeWillReachWarning(props: BasketSizeWillReachWarningProps) {
  const { modalState, shift, rateNegotiationId } = props;
  const { id: shiftId } = shift;

  const { userId } = useDefinedWorker();
  const subStageValues = useFetchSubstage();

  const { data: { clipboardScore } = {} } = useGetClipboardScoreProfile(userId, {
    enabled: !isEmpty(userId),
  });

  const remainingPointsNeeded =
    isDefined(clipboardScore) && isDefined(subStageValues?.data?.accessMetadata?.scoreThreshold)
      ? subStageValues.data.accessMetadata.scoreThreshold - clipboardScore
      : undefined;

  const { navigateToModal, computedArrivalData } = useShiftModalsDataContext();
  const bookShiftParams = convertModalShiftDataToUseBookShiftParams({
    shift,
    navigateToModal,
    onBook: () => {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_BOOKED_MODAL_PATH, {
        pathParams: { shiftId },
        clearExistingSearchParams: Boolean(rateNegotiationId),
      });
    },
    computedArrivalData,
    rateNegotiationId,
  });

  const {
    attemptBookingShift,
    isBooking,
    isLoading: isLoadingUseBookShift,
  } = useBookShift(bookShiftParams);

  const { data: qualificationDisplayLabels } = useGetQualificationDisplayLabels(
    subStageValues.data?.qualifications ?? []
  );

  return (
    <BottomSheet modalState={modalState}>
      <BottomSheetIllustrationContent
        illustrationType="limited-substage-badge"
        title="Heads up!"
        description={
          <Stack mx={6}>
            <Text variant="body1" textAlign="left">
              While in limited status, You{" "}
              <b>won&apos;t be able to book more {qualificationDisplayLabels.join(", ")} shifts</b>{" "}
              until you work this one (with some exceptions).
            </Text>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <br />
            {remainingPointsNeeded && (
              <Text variant="body1" textAlign="left">
                You have {remainingPointsNeeded} points to go to unlock full access!
              </Text>
            )}
          </Stack>
        }
      />
      <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
        <LoadingButton
          fullWidth
          variant="contained"
          size="large"
          isLoading={isLoadingUseBookShift || isBooking}
          onClick={async () => {
            // We need to set the basket size warning acknowledgment here
            // If we don't do this, the user will see the basket size warning modal infinitely
            setBasketSizeWarningAcknowledgment(shiftId);
            await attemptBookingShift({
              skipChecks: [PreBookingCheck.BASKET_SIZE_WILL_REACH_WARNING],
            });
          }}
        >
          I understand, continue
        </LoadingButton>
      </DialogFooter>
    </BottomSheet>
  );
}
